import DOMPurify from "dompurify"
import React, { useMemo } from "react"

import type { CitationView } from "../../containers/Lead/LeadWorkspacePage/education/types/education.types.ts"
import { cn } from "../../utils/cn.ts"
import "./education-article.css"

// The backend template (`project_education/render.py`) only ever emits this
// tag set — no `a`, no `img` — but a sanitizer exists precisely so a
// compromised or legacy edition row is never trusted just because it's the
// server that sent it.
const ALLOWED_TAGS = [
  "h2",
  "h3",
  "p",
  "ul",
  "ol",
  "li",
  "sup",
  "section",
  "article",
  "strong",
  "em"
]

// `render.py` only ever puts these classes on `<p>` and `<section>`. Any
// other value (a utility class such as `fixed inset-0`) could restyle the
// page around the article, so only these survive, and only on those tags.
const CLASS_ALLOWED_TAGS = new Set(["section", "p"])
const ALLOWED_CLASSES = new Set([
  "summary",
  "section-citations",
  "limitations",
  "next-actions"
])

const stripDisallowedClass = (node: Element) => {
  if (!node.hasAttribute("class")) return
  const kept = CLASS_ALLOWED_TAGS.has(node.tagName.toLowerCase())
    ? Array.from(node.classList).filter((name) => ALLOWED_CLASSES.has(name))
    : []
  if (kept.length) node.setAttribute("class", kept.join(" "))
  else node.removeAttribute("class")
}

/** Added and removed around one call, never left on the shared DOMPurify
 * singleton, where it would strip `class` from every other consumer too. */
const sanitizeEducationHtml = (html: string): string => {
  DOMPurify.addHook("afterSanitizeAttributes", stripDisallowedClass)
  try {
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS,
      ALLOWED_ATTR: ["class"],
      ALLOW_DATA_ATTR: false,
      ALLOW_ARIA_ATTR: false
    })
  } finally {
    DOMPurify.removeHook("afterSanitizeAttributes", stripDisallowedClass)
  }
}

const isSafeCitationUrl = (url: string | null): url is string =>
  !!url && /^https?:\/\//i.test(url)

export interface EducationArticleLabels {
  /** "View sources (N)" — takes the citation count since it's the only
   * piece of copy here that interpolates. */
  viewSources: (count: number) => string
  sourcesLabel: string
}

interface EducationArticleProps {
  html: string
  citations: CitationView[]
  /** When given, a non-empty citation list renders as one "View sources"
   * trigger instead of an inline list — the caller (e.g. a mobile bottom
   * sheet) owns presenting the full list. */
  onOpenSources?: () => void
  className?: string
  labels: EducationArticleLabels
}

/**
 * Renders a generated edition's article body. Shared verbatim by the
 * internal sales-brief view and the customer-facing guide — it carries no
 * audience-specific chrome, only the sanitized content and its citations.
 *
 * Copy comes in as an explicit `labels` prop, the same way
 * `EducationAudioPlayer` takes one, rather than an internal
 * `useTranslation()` call — the customer-facing guide can mount this
 * component outside the authenticated app's i18n bundle entirely.
 */
export const EducationArticle = ({
  html,
  citations,
  onOpenSources,
  className,
  labels
}: EducationArticleProps) => {
  const sanitized = useMemo(() => sanitizeEducationHtml(html), [html])

  return (
    <div className={cn("education-article", className)}>
      <div dangerouslySetInnerHTML={{ __html: sanitized }} />
      {citations.length > 0 &&
        (onOpenSources ? (
          <button
            type="button"
            onClick={onOpenSources}
            className="mt-4 border-none bg-transparent p-0 text-sm text-blue-600 underline"
          >
            {labels.viewSources(citations.length)}
          </button>
        ) : (
          <ol
            className="mt-4 list-decimal space-y-1 pl-5 text-sm text-gray-600"
            aria-label={labels.sourcesLabel}
          >
            {citations.map((citation) => (
              <li key={citation.id}>
                {isSafeCitationUrl(citation.url) ? (
                  <a
                    href={citation.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {citation.label}
                  </a>
                ) : (
                  citation.label
                )}
              </li>
            ))}
          </ol>
        ))}
    </div>
  )
}
